<script lang="ts" setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from "vue";
import {
  useProjectWizardSteps,
  useSaveWizardConfiguration,
  type ProjectWizardEntryView,
} from "@/queries/projects";

type StepComponentType = "vueform" | "custom" | "connection" | string;

interface DisplayStep {
  id: string;
  stepTitle: string;
  description: string;
  componentType: StepComponentType;
  vueformSchema: Record<string, unknown> | null;
  customTemplate: { name?: string; componentName?: string; description?: string; icon?: string } | null;
  /** Required steps gate workflow completion; optional steps don't. From the wizard schema. */
  isRequired: boolean;
}

interface DisplayWorkflow {
  id: string;
  workflowName: string;
  description: string;
  versionLabel: string;
  isDeprecatedByPublisher: boolean;
  isCustomized: boolean;
  steps: DisplayStep[];
}

interface Props {
  project: { id: string; name?: string; projectType?: "DIY" | "PublisherMaster" | "Subscriber" } | null | undefined;
  show: boolean;
}

interface Emits {
  (e: "close"): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const projectId = computed(() => props.project?.id ?? "");
const enabled = computed(() => !!props.show && !!projectId.value);
const { wizardSteps, wizardStepsLoading, wizardStepsError } = useProjectWizardSteps(projectId, enabled);
const { saveWizardConfigurationAsync, saveWizardConfigurationPending, saveWizardConfigurationError } =
  useSaveWizardConfiguration(projectId);

// Local mutable copy of the workflow collections — completion state is local-only for now.
const workflowCollections = ref<DisplayWorkflow[]>([]);

// Form data keyed by workflow id — matches the backend storage shape
// (Project.WorkflowWizardConfigurations: workflowId -> field bag, top-level since Phase 1).
// All vueform fields within a workflow share one bag, so field names should be unique across
// that workflow's wizard steps.
const formDataByWorkflow = ref<Record<string, Record<string, unknown>>>({});

// Extract the vueform schema saved by the publisher's WorkflowWizardBuilder. Backend stores it
// under Properties["vueformConfig"]["schema"] (see wizard-mappers.ts toBackendStep).
function extractVueformSchema(properties: Record<string, unknown>): Record<string, unknown> | null {
  const cfg = properties?.vueformConfig as { schema?: Record<string, unknown> } | undefined;
  return cfg?.schema && Object.keys(cfg.schema).length > 0 ? cfg.schema : null;
}

function extractCustomTemplate(properties: Record<string, unknown>): DisplayStep["customTemplate"] {
  const tpl = properties?.customTemplate as DisplayStep["customTemplate"];
  return tpl ?? null;
}

// Map API entries to the display shape. Active wizard definitions only; their steps flattened
// in their natural order (steps within a definition are pre-sorted by Order on the backend).
function mapEntries(entries: ProjectWizardEntryView[]): DisplayWorkflow[] {
  return entries.map((entry) => {
    const steps: DisplayStep[] = entry.wizardDefinitions
      .filter((def) => def.isActive)
      .flatMap((def) => def.steps)
      .map((step) => {
        const props = (step.properties ?? {}) as Record<string, unknown>;
        const componentType = step.componentType ?? "vueform";
        return {
          id: step.id,
          stepTitle: step.name,
          description: stepDescription(componentType),
          componentType,
          vueformSchema: componentType === "vueform" ? extractVueformSchema(props) : null,
          customTemplate: componentType === "custom" ? extractCustomTemplate(props) : null,
          isRequired: step.isRequired,
        };
      });

    return {
      id: entry.workflowId,
      workflowName: entry.workflowName,
      description: `${entry.versionLabel}${entry.isCustomized ? " · customized" : ""}`,
      versionLabel: entry.versionLabel,
      isDeprecatedByPublisher: entry.isDeprecatedByPublisher,
      isCustomized: entry.isCustomized,
      steps,
    };
  });
}

/**
 * Workflow-level completion derived from the API's view of saved configuration.
 * Updates after each save round-trip; not live-typed.
 */
function workflowCompletion(workflowId: string): { isComplete: boolean; missingStepIds: Set<string> } {
  const entry = wizardSteps.value?.entries.find((e) => e.workflowId === workflowId);
  if (!entry) return { isComplete: true, missingStepIds: new Set() };
  return {
    isComplete: entry.completion.isComplete,
    missingStepIds: new Set(entry.completion.missingSteps.map((m) => m.stepId)),
  };
}

/**
 * Flattened "{ workflowName, stepName }" pairs for every step that became required since the
 * subscriber's previous product version. Empty for fresh subscribers and master/DIY views.
 * Drives the upgrade-induced banner at the top of the wizard modal so the subscriber sees
 * which steps are newly required rather than guessing why "Setup Required" suddenly appeared.
 */
const newlyRequiredMissing = computed(() => {
  const sequence = wizardSteps.value;
  if (!sequence) return [] as { workflowName: string; stepName: string }[];
  const out: { workflowName: string; stepName: string }[] = [];
  for (const entry of sequence.entries) {
    for (const step of entry.newlyRequiredMissingSteps ?? []) {
      out.push({ workflowName: entry.workflowName, stepName: step.stepName });
    }
  }
  return out;
});

/**
 * Step-level "filled" state — derived locally from the working form data so the visual updates
 * as the user types. Falls back to the API view if no form data has been loaded yet.
 */
function isStepFilled(workflowId: string, step: DisplayStep): boolean {
  const bag = formDataByWorkflow.value[workflowId] ?? {};
  const hasInBag = step.id in bag && bag[step.id] !== null && bag[step.id] !== "" && bag[step.id] !== undefined;
  if (hasInBag) return true;
  // Multi-field vueform steps: at least one vueform field has a value.
  if (step.vueformSchema) {
    const fields = Object.keys(step.vueformSchema);
    return fields.some((f) => {
      const v = bag[f];
      return v !== null && v !== undefined && v !== "";
    });
  }
  return false;
}

function isStepComplete(workflowId: string, step: DisplayStep): boolean {
  // Optional steps don't gate completion; mark them complete trivially.
  if (!step.isRequired) return true;
  // Required step: API's missingSteps list is authoritative. If the API hasn't returned (yet)
  // or doesn't list it, fall back to the live form-data check.
  const { missingStepIds } = workflowCompletion(workflowId);
  if (missingStepIds.has(step.id)) return false;
  return isStepFilled(workflowId, step);
}

function isWorkflowComplete(workflowId: string): boolean {
  return workflowCompletion(workflowId).isComplete;
}

function stepDescription(componentType: string): string {
  switch (componentType) {
    case "vueform": return "Form";
    case "custom": return "Custom step";
    case "connection": return "Connection";
    default: return componentType;
  }
}

const currentStepIndex = ref(0);
const currentSubStepIndex = ref(0);
// Tracks which project the navigation indices were last initialized for. A change in this
// value means we're looking at a different project and the indices should snap to 0; a
// same-project refetch (e.g. after saveWizardConfiguration invalidates the query) leaves
// the user's position intact — otherwise Next would save, refetch, and snap navigation back.
const lastHydratedProjectId = ref<string | null>(null);

watch(
  () => wizardSteps.value,
  (sequence) => {
    workflowCollections.value = sequence ? mapEntries(sequence.entries) : [];

    // Hydrate per-workflow form data from previously saved configuration.
    formDataByWorkflow.value = {};
    if (sequence) {
      for (const entry of sequence.entries) {
        formDataByWorkflow.value[entry.workflowId] = {
          ...(entry.savedConfiguration ?? {}),
        };
      }
    }

    // Only reset navigation on a true project switch — not on a same-project refetch.
    const newProjectId = projectId.value || null;
    if (newProjectId !== lastHydratedProjectId.value) {
      currentStepIndex.value = 0;
      currentSubStepIndex.value = 0;
      lastHydratedProjectId.value = newProjectId;
    }
  },
  { immediate: true }
);

const currentWizardStep = computed<DisplayWorkflow | undefined>(
  () => workflowCollections.value[currentStepIndex.value]
);

const currentSubStep = computed<DisplayStep | null>(() => {
  const wf = currentWizardStep.value;
  if (!wf || wf.steps.length === 0) return null;
  return wf.steps[currentSubStepIndex.value] ?? null;
});

const totalWizardSteps = computed(() => workflowCollections.value.length);

const goToStep = (stepIndex: number, subStepIndex = 0) => {
  if (stepIndex >= 0 && stepIndex < totalWizardSteps.value) {
    currentStepIndex.value = stepIndex;
    currentSubStepIndex.value = subStepIndex;
  }
};

const goToSubStep = (subStepIndex: number) => {
  const wf = currentWizardStep.value;
  if (wf && subStepIndex >= 0 && subStepIndex < wf.steps.length) {
    currentSubStepIndex.value = subStepIndex;
  }
};

// The progress line spans first-dot-center to last-dot-center, measured from the rendered dots
// (not estimated from fixed row heights) so it tracks any step count, wrapping, or badge change.
// It sits behind the dots, so running a few pixels into each end dot is hidden by the dot.
const stepsContent = ref<HTMLElement | null>(null);
const lineTop = ref(0);
const lineHeight = ref(0);

// Every dot carries data-wizard-dot; querying them at measure time (rather than collecting via
// ref callbacks) gives them in document order with no accumulation or stale-element bookkeeping.
function measureLine() {
  const container = stepsContent.value;
  if (!container) {
    lineHeight.value = 0;
    return;
  }
  const dots = container.querySelectorAll<HTMLElement>("[data-wizard-dot]");
  if (dots.length === 0) {
    lineHeight.value = 0;
    return;
  }
  const containerTop = container.getBoundingClientRect().top;
  const firstDot = dots[0].getBoundingClientRect();
  const lastDot = dots[dots.length - 1].getBoundingClientRect();
  const firstCenter = firstDot.top - containerTop + firstDot.height / 2;
  const lastCenter = lastDot.top - containerTop + lastDot.height / 2;
  lineTop.value = firstCenter;
  lineHeight.value = Math.max(0, lastCenter - firstCenter);
}

// A ResizeObserver on the steps container re-measures on any layout change (wrapping, badges, the
// list growing) without us tracking each cause; the watch below handles the initial data render.
let resizeObserver: ResizeObserver | null = null;

watch(
  () => workflowCollections.value,
  async () => {
    await nextTick();
    measureLine();
  },
  { flush: "post" }
);

watch(stepsContent, (el) => {
  resizeObserver?.disconnect();
  if (el) {
    resizeObserver = new ResizeObserver(() => measureLine());
    resizeObserver.observe(el);
  }
});

onBeforeUnmount(() => resizeObserver?.disconnect());

const currentFormData = computed<Record<string, unknown>>({
  get: () => {
    const wf = currentWizardStep.value;
    if (!wf) return {};
    return formDataByWorkflow.value[wf.id] ?? {};
  },
  set: (value) => {
    const wf = currentWizardStep.value;
    if (!wf) return;
    // MERGE rather than replace. Each sub-step renders its own Vueform (keyed by sub-step id),
    // and Vueform's v-model emit only carries the fields in *that step's* schema. A naive
    // replace wipes every other step's values from the workflow bag the moment the user
    // navigates between steps — including data already saved to the backend, because the next
    // Save & Close round-trip sends the truncated bag back.
    formDataByWorkflow.value[wf.id] = {
      ...(formDataByWorkflow.value[wf.id] ?? {}),
      ...value,
    };
  },
});

// Phase 1: wizard answers persist for all project types. The endpoint no longer guards on
// project type — master/DIY use the same save path as subscribers.
const canPersistConfiguration = computed(() => !!props.project?.projectType);

/**
 * Returns true if the save succeeded (or was a no-op skip); false if the save failed.
 * Callers should keep the user on the current step when false — surface the error instead.
 */
async function saveCurrentWorkflowConfiguration(): Promise<boolean> {
  if (!canPersistConfiguration.value) return true;
  const wf = currentWizardStep.value;
  if (!wf) return true;
  const data = formDataByWorkflow.value[wf.id] ?? {};
  // Skip the round trip when the workflow has no editable fields — purely custom/connection
  // workflows have nothing to persist yet.
  const hasAnyVueformStep = wf.steps.some((s) => s.componentType === "vueform" && s.vueformSchema);
  if (!hasAnyVueformStep) return true;
  try {
    await saveWizardConfigurationAsync({ workflowId: wf.id, configuration: data });
    return true;
  } catch {
    // Error is exposed on saveWizardConfigurationError for the inline banner.
    return false;
  }
}

const nextStep = async () => {
  const wf = currentWizardStep.value;
  if (!wf) return;

  // Save on every Next — including between sub-steps in the same workflow. Gives the user
  // immediate completion feedback (dots turn green after the round-trip) and means closing
  // the wizard mid-flight doesn't lose recent input. If the save fails, stay on the step
  // and let the inline banner explain — silently advancing would lose the user's input.
  const saved = await saveCurrentWorkflowConfiguration();
  if (!saved) return;

  // If we're inside a workflow's sub-step list, advance through them first.
  if (currentSubStep.value && currentSubStepIndex.value < wf.steps.length - 1) {
    currentSubStepIndex.value++;
    return;
  }

  // Otherwise, advance to the next workflow.
  if (currentStepIndex.value < totalWizardSteps.value - 1) {
    currentStepIndex.value++;
    currentSubStepIndex.value = 0;
  }
};

const previousStep = async () => {
  // Save before navigating backward too — same rationale as nextStep.
  const saved = await saveCurrentWorkflowConfiguration();
  if (!saved) return;

  if (currentSubStep.value && currentSubStepIndex.value > 0) {
    currentSubStepIndex.value--;
    return;
  }

  if (currentStepIndex.value > 0) {
    currentStepIndex.value--;
    const prev = workflowCollections.value[currentStepIndex.value];
    currentSubStepIndex.value = prev && prev.steps.length > 0 ? prev.steps.length - 1 : 0;
  }
};

const hasNextStep = computed(() => {
  const wf = currentWizardStep.value;
  if (!wf) return false;
  if (currentSubStep.value && currentSubStepIndex.value < wf.steps.length - 1) return true;
  return currentStepIndex.value < totalWizardSteps.value - 1;
});

// Calculate current visual position for dot styling — counts each rendered dot
// (one per workflow header, one per sub-step) up to the current location.
const currentVisualPosition = computed(() => {
  let position = 0;
  for (let i = 0; i < currentStepIndex.value; i++) {
    position++; // workflow header
    position += workflowCollections.value[i]?.steps.length ?? 0;
  }
  // Current workflow header
  if (currentStepIndex.value < workflowCollections.value.length) {
    position++;
    if (currentSubStep.value) position += currentSubStepIndex.value;
  }
  return position;
});

// Completion is derived from saved configuration vs. required-step list — see workflowCompletion
// and isStepComplete. No manual "mark as complete" mechanism.

/**
 * Close handler — persist any unsaved changes on the current workflow before emitting close.
 * Save is fire-and-forget here (no confirm dialog needed) because every Next/Previous already
 * persists; the only window where unsaved data could exist is "user typed, then clicked X
 * without navigating." We catch that case silently rather than block with a confirm.
 */
async function handleClose() {
  try {
    await saveCurrentWorkflowConfiguration();
  } catch {
    // Best-effort save on close. The user is leaving anyway; we don't want to trap them.
  }
  emit("close");
}
</script>

<template>
  <div
    v-if="show"
    class="modal fade show d-block"
    tabindex="-1"
    @click="handleClose"
  >
    <div class="modal-dialog modal-xl" @click.stop>
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header">
          <div>
            <h2 class="modal-title mb-0">Project Setup Wizard</h2>
            <small class="text-muted">{{ project?.name }}</small>
          </div>
          <button type="button" class="btn-close" @click="handleClose"></button>
        </div>

        <!-- Modal Body -->
        <div class="modal-body p-0" style="height: 500px">
          <!-- Loading / error / empty states -->
          <div v-if="wizardStepsLoading" class="d-flex align-items-center justify-content-center h-100">
            <div class="spinner-border text-secondary" role="status">
              <span class="visually-hidden">Loading wizard…</span>
            </div>
          </div>
          <div v-else-if="wizardStepsError" class="p-4">
            <div class="alert alert-danger mb-0">
              <i class="bi bi-exclamation-triangle me-2"></i>
              Failed to load wizard steps.
              <span v-if="wizardStepsError.message"> {{ wizardStepsError.message }}</span>
            </div>
          </div>
          <div v-else-if="totalWizardSteps === 0" class="p-4">
            <div class="alert alert-info mb-0">
              <i class="bi bi-info-circle me-2"></i>
              No wizard steps are defined for this project's current product version yet.
            </div>
          </div>

          <!-- Full-height flex column: the banner + mobile bar take their natural height and the
               step row flex-fills the rest. Without a fixed-height ancestor the row's children
               can't scroll, so the step list overflows the modal instead. -->
          <div v-else class="d-flex flex-column h-100">
            <!-- Banner: required steps the most recent product upgrade introduced. Shows only
                 when the subscriber's project has been through at least one version bump AND
                 the upgrade added required steps that aren't filled in. Frames "Setup Required"
                 as "publisher added new fields" rather than "you never finished setup." -->
            <div v-if="newlyRequiredMissing.length > 0" class="px-3 pt-3">
              <div class="alert alert-info mb-0 small">
                <i class="bi bi-info-circle me-2"></i>
                A recent product update added
                <strong>{{ newlyRequiredMissing.length }}</strong>
                required {{ newlyRequiredMissing.length === 1 ? 'step' : 'steps' }} that need your input:
                <ul class="mb-0 mt-1">
                  <li v-for="(item, idx) in newlyRequiredMissing" :key="idx">
                    <span class="fw-medium">{{ item.workflowName }}</span>
                    <span class="text-muted"> · </span>
                    {{ item.stepName }}
                  </li>
                </ul>
              </div>
            </div>

            <!-- Mobile Progress Bar -->
            <div class="d-lg-none border-bottom bg-body-tertiary p-3">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <span class="fw-medium">{{ currentWizardStep?.workflowName || "Step" }}</span>
                <small class="text-muted">{{ currentStepIndex + 1 }} of {{ totalWizardSteps }}</small>
              </div>
              <div class="progress mb-2" style="height: 6px">
                <div
                  class="progress-bar"
                  :style="`width: ${((currentStepIndex + 1) / totalWizardSteps) * 100}%; background-color: var(--bs-secondary);`"
                ></div>
              </div>
              <div class="d-flex justify-content-between gap-2">
                <button
                  class="bb-btn-secondary-cta btn-sm"
                  :disabled="currentStepIndex === 0 && currentSubStepIndex === 0"
                  @click="previousStep"
                >
                  <i class="bi bi-chevron-left"></i> Previous
                </button>
                <button
                  v-if="hasNextStep"
                  class="bb-btn-primary-cta btn-sm"
                  @click="nextStep"
                >
                  Next <i class="bi bi-chevron-right"></i>
                </button>
                <button
                  v-else
                  class="bb-btn-primary-cta btn-sm"
                  :disabled="saveWizardConfigurationPending"
                  @click="handleClose"
                >
                  <i class="bi bi-check-circle"></i> Save &amp; Close
                </button>
              </div>
            </div>

            <!-- flex-grow-1 fills the space under the banner/mobile bar; min-height: 0 lets this
                 row shrink below its content so the sidebar + content panes scroll internally. -->
            <div class="row g-0 flex-grow-1" style="min-height: 0">
              <!-- Left Sidebar: Navigation -->
              <div
                class="col-lg-3 border-end bg-body-tertiary d-none d-lg-block"
                style="height: 100%; overflow: hidden"
              >
                <div
                  class="wizard-steps-container p-3"
                  style="height: 100%; overflow-y: scroll"
                >
                  <div ref="stepsContent" class="position-relative wizard-steps-content">
                    <!-- Vertical line; top/height measured from the dots — see measureLine. -->
                    <div
                      class="position-absolute bg-secondary wizard-progress-line"
                      :style="{
                        left: '22px',
                        top: lineTop + 'px',
                        width: '3px',
                        height: lineHeight + 'px',
                        zIndex: 1,
                        backgroundColor: 'var(--bs-secondary)',
                      }"
                    ></div>

                    <!-- Workflows -->
                    <div
                      v-for="(workflow, workflowIndex) in workflowCollections"
                      :key="workflow.id"
                    >
                      <!-- Main workflow step -->
                      <div
                        class="d-flex align-items-center gap-3 py-3 user-select-none"
                        @click="goToStep(workflowIndex)"
                        style="margin-left: 8px; cursor: pointer; position: relative; z-index: 2;"
                      >
                        <div
                          data-wizard-dot
                          class="rounded-circle d-flex align-items-center justify-content-center"
                          :style="{
                            width: '32px',
                            height: '32px',
                            minWidth: '32px',
                            backgroundColor:
                              currentVisualPosition >
                              workflowCollections.slice(0, workflowIndex).reduce((sum, w) => sum + 1 + w.steps.length, 0)
                                ? 'var(--bs-secondary)'
                                : 'var(--bs-tertiary-bg)',
                            border: '2px solid var(--bs-secondary)',
                          }"
                        >
                          <i
                            class="bi bi-diagram-3"
                            :style="{
                              color:
                                currentVisualPosition >
                                workflowCollections.slice(0, workflowIndex).reduce((sum, w) => sum + 1 + w.steps.length, 0)
                                  ? 'var(--bs-dark)'
                                  : 'var(--bs-secondary)',
                              fontSize: '16px',
                            }"
                          ></i>
                        </div>
                        <span
                          class="fw-medium"
                          :class="{
                            'text-primary': currentStepIndex === workflowIndex,
                            'text-success': isWorkflowComplete(workflow.id) && currentStepIndex !== workflowIndex,
                            'text-dark': !isWorkflowComplete(workflow.id) && currentStepIndex !== workflowIndex,
                          }"
                        >
                          {{ workflow.workflowName }}
                          <span
                            v-if="workflow.isDeprecatedByPublisher"
                            class="badge bg-warning text-dark ms-1"
                            style="font-size: 0.6rem"
                            title="The publisher has removed this workflow from the current product version. Your customization keeps it active."
                          >Deprecated</span>
                          <span
                            v-if="workflow.isCustomized"
                            class="badge bg-info text-dark ms-1"
                            style="font-size: 0.6rem"
                            title="You have customized this workflow — it's pinned to your forked version."
                          >Customized</span>
                        </span>
                      </div>

                      <!-- Sub-steps -->
                      <div
                        v-for="(step, stepIndex) in workflow.steps"
                        :key="step.id"
                        class="d-flex align-items-center gap-3 py-2 user-select-none"
                        style="margin-left: 16px; cursor: pointer"
                        @click="goToStep(workflowIndex, stepIndex)"
                      >
                        <div
                          data-wizard-dot
                          class="rounded-circle"
                          :style="{
                            width: '16px',
                            height: '16px',
                            minWidth: '16px',
                            backgroundColor:
                              currentVisualPosition >
                              workflowCollections.slice(0, workflowIndex).reduce((sum, w) => sum + 1 + w.steps.length, 0) + 1 + stepIndex
                                ? 'var(--bs-secondary)'
                                : 'var(--bs-tertiary-bg)',
                            border: '2px solid var(--bs-secondary)',
                          }"
                        ></div>
                        <span
                          :class="{
                            'fw-bold text-primary': currentStepIndex === workflowIndex && currentSubStepIndex === stepIndex,
                            'text-success': isStepComplete(workflow.id, step) && !(currentStepIndex === workflowIndex && currentSubStepIndex === stepIndex),
                            'text-muted': !isStepComplete(workflow.id, step) && !(currentStepIndex === workflowIndex && currentSubStepIndex === stepIndex),
                          }"
                        >
                          {{ step.stepTitle }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right Content Area -->
              <div class="col-12 col-lg-9">
                <div class="p-4 h-100 overflow-auto">
                  <div v-if="currentWizardStep">
                    <!-- Workflow Overview (shown when no sub-step selected, or workflow has no sub-steps) -->
                    <div v-if="!currentSubStep">
                      <div class="mb-4">
                        <div class="d-flex align-items-center gap-3 mb-3">
                          <div
                            class="d-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10"
                            style="width: 60px; height: 60px"
                          >
                            <i class="bi bi-diagram-3 text-primary" style="font-size: 1.5rem"></i>
                          </div>
                          <div>
                            <h3 class="mb-1">
                              {{ currentWizardStep.workflowName }}
                              <span
                                v-if="currentWizardStep.isDeprecatedByPublisher"
                                class="badge bg-warning text-dark ms-2"
                                style="font-size: 0.7rem; vertical-align: middle"
                              >Deprecated by publisher</span>
                              <span
                                v-if="currentWizardStep.isCustomized"
                                class="badge bg-info text-dark ms-2"
                                style="font-size: 0.7rem; vertical-align: middle"
                              >Customized</span>
                            </h3>
                            <p class="text-muted mb-0">{{ currentWizardStep.description }}</p>
                            <span class="badge bg-primary rounded-pill mt-1"
                              >{{ currentWizardStep.steps.length }} steps</span
                            >
                          </div>
                        </div>

                        <div
                          v-if="currentWizardStep.isDeprecatedByPublisher"
                          class="alert alert-warning small mb-0 mt-2"
                        >
                          <i class="bi bi-exclamation-triangle me-2"></i>
                          The publisher has removed this workflow from the latest product version.
                          Your customization keeps it active for this project.
                        </div>
                      </div>

                      <div v-if="currentWizardStep.steps.length === 0" class="alert alert-info">
                        <i class="bi bi-info-circle me-2"></i>
                        This workflow has no wizard steps configured.
                      </div>

                      <!-- Steps Overview -->
                      <div v-else class="row g-3">
                        <div
                          v-for="(step, index) in currentWizardStep.steps"
                          :key="step.id"
                          class="col-12"
                        >
                          <div
                            class="card"
                            :class="{ 'border-success': isStepComplete(currentWizardStep.id, step) }"
                          >
                            <div class="card-body">
                              <div class="d-flex align-items-center justify-content-between">
                                <div class="d-flex align-items-center gap-3">
                                  <div
                                    class="rounded-circle d-flex align-items-center justify-content-center"
                                    :class="{
                                      'bg-success': isStepComplete(currentWizardStep.id, step),
                                      'bg-secondary': !isStepComplete(currentWizardStep.id, step),
                                    }"
                                    style="width: 30px; height: 30px"
                                  >
                                    <i v-if="isStepComplete(currentWizardStep.id, step)" class="bi bi-check text-white"></i>
                                    <span v-else class="text-white small">{{ index + 1 }}</span>
                                  </div>
                                  <div>
                                    <h4 class="mb-1">{{ step.stepTitle }}</h4>
                                    <small class="text-muted">
                                      {{ step.description }}
                                      <span v-if="step.isRequired" class="text-danger ms-1">*</span>
                                    </small>
                                  </div>
                                </div>
                                <button
                                  class="bb-btn-secondary-cta btn-sm"
                                  @click="goToSubStep(index)"
                                >
                                  {{ isStepComplete(currentWizardStep.id, step) ? "Review" : "Configure" }}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="mt-4 d-none d-lg-flex justify-content-between">
                        <button
                          class="bb-btn-secondary-cta"
                          :disabled="currentStepIndex === 0 && currentSubStepIndex === 0"
                          @click="previousStep"
                        >
                          <i class="bi bi-arrow-left me-1"></i> Previous
                        </button>
                        <button v-if="hasNextStep" class="bb-btn-primary-cta" @click="nextStep">
                          Next Workflow <i class="bi bi-arrow-right ms-1"></i>
                        </button>
                        <button
                          v-else
                          class="bb-btn-primary-cta"
                          :disabled="saveWizardConfigurationPending"
                          @click="handleClose"
                        >
                          <i class="bi bi-check-circle me-1"></i>Save &amp; Close
                        </button>
                      </div>
                    </div>

                    <!-- Individual Sub-Step -->
                    <div v-else>
                      <div class="mb-4">
                        <div class="d-flex align-items-center gap-3 mb-3">
                          <div
                            class="d-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10"
                            style="width: 60px; height: 60px"
                          >
                            <i class="bi bi-list-task text-primary" style="font-size: 1.5rem"></i>
                          </div>
                          <div class="flex-grow-1">
                            <h3 class="mb-0">{{ currentSubStep.stepTitle }}</h3>
                          </div>
                        </div>
                      </div>

                      <div class="card">
                        <div class="card-body">
                          <!-- Vueform step: render the publisher-authored schema.
                               IMPORTANT: pass saved values via :default, not just v-model.
                               Vueform initializes its internal data from per-element defaults
                               on mount; if v-model has values the schema doesn't, Vueform
                               overwrites v-model back to its (empty) internal state in onMounted.
                               The form-level :default prop seeds each element as a fallback
                               default keyed by element name (see useDefault.js in @vueform/vueform).
                               Without this, going Previous to a previously-answered step shows
                               the field empty even though the value is saved on the server. -->
                          <div
                            v-if="currentSubStep.componentType === 'vueform' && currentSubStep.vueformSchema"
                            class="form-container mb-3"
                          >
                            <Vueform
                              :key="currentSubStep.id"
                              :schema="(currentSubStep.vueformSchema as any)"
                              :default="(currentFormData as any)"
                              v-model="currentFormData"
                              :columns="{ label: 12, wrapper: 12 }"
                              :endpoint="false"
                            />
                          </div>
                          <div
                            v-else-if="currentSubStep.componentType === 'vueform'"
                            class="alert alert-warning mb-3"
                          >
                            <i class="bi bi-exclamation-triangle me-2"></i>
                            This step has no fields configured by the publisher yet.
                          </div>

                          <!-- Custom step placeholder (component is referenced by name only for now) -->
                          <div
                            v-else-if="currentSubStep.componentType === 'custom'"
                            class="alert alert-info mb-3"
                          >
                            <i class="bi bi-puzzle me-2"></i>
                            Custom component:
                            <strong>{{ currentSubStep.customTemplate?.name || currentSubStep.customTemplate?.componentName || "unknown" }}</strong>
                            <div v-if="currentSubStep.customTemplate?.description" class="small text-muted mt-1">
                              {{ currentSubStep.customTemplate.description }}
                            </div>
                          </div>

                          <!-- Connection step placeholder -->
                          <div
                            v-else-if="currentSubStep.componentType === 'connection'"
                            class="alert alert-info mb-3"
                          >
                            <i class="bi bi-plug me-2"></i>
                            Connection configuration will be wired into this step.
                          </div>

                          <!-- Unknown component type fallback -->
                          <div v-else class="alert alert-secondary mb-3">
                            <i class="bi bi-question-circle me-2"></i>
                            Unsupported step type: <code>{{ currentSubStep.componentType }}</code>
                          </div>

                          <div
                            v-if="saveWizardConfigurationError"
                            class="alert alert-danger small mb-2"
                          >
                            <i class="bi bi-exclamation-triangle me-2"></i>
                            Couldn't save your answers.
                            <span v-if="saveWizardConfigurationError.userMessage">
                              {{ saveWizardConfigurationError.userMessage }}
                            </span>
                            <span v-else-if="saveWizardConfigurationError.message">
                              {{ saveWizardConfigurationError.message }}
                            </span>
                          </div>
                          <div class="d-flex gap-2 justify-content-between d-none d-lg-flex">
                            <button
                              class="bb-btn-secondary-cta"
                              :disabled="(currentStepIndex === 0 && currentSubStepIndex === 0) || saveWizardConfigurationPending"
                              @click="previousStep"
                            >
                              <i class="bi bi-arrow-left me-1"></i> Previous
                            </button>
                            <div class="d-flex gap-2 align-items-center">
                              <small v-if="saveWizardConfigurationPending" class="text-muted">
                                <i class="bi bi-arrow-repeat spin me-1"></i>Saving…
                              </small>
                              <button
                                v-if="hasNextStep"
                                class="bb-btn-primary-cta"
                                :disabled="saveWizardConfigurationPending"
                                @click="nextStep"
                              >
                                Next <i class="bi bi-arrow-right ms-1"></i>
                              </button>
                              <button
                                v-else
                                class="bb-btn-primary-cta"
                                :disabled="saveWizardConfigurationPending"
                                @click="handleClose"
                              >
                                <i class="bi bi-check-circle me-1"></i>Save &amp; Close
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.modal {
  background-color: rgba(0, 0, 0, 0.5);
}

// Theme-aware scrollbar — Bootstrap CSS vars flip with data-bs-theme so it works in dark mode.
.wizard-steps-container {
  scrollbar-width: thin;
  scrollbar-color: var(--bs-border-color) var(--bs-secondary-bg);
}

.wizard-steps-container::-webkit-scrollbar {
  width: 8px;
}

.wizard-steps-container::-webkit-scrollbar-track {
  background: var(--bs-secondary-bg);
  border-radius: 4px;
}

.wizard-steps-container::-webkit-scrollbar-thumb {
  background-color: var(--bs-border-color);
  border-radius: 4px;
}

.wizard-steps-container::-webkit-scrollbar-thumb:hover {
  background-color: var(--bs-secondary-color);
}

.wizard-steps-content {
  padding-bottom: 20px;
}

.wizard-steps-content > div {
  position: relative;
  z-index: 2;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
.spin {
  display: inline-block;
  animation: spin 0.75s linear infinite;
}

/*
 * Vueform's bootstrap theme generates vf-col-N classes via its columns()
 * function but ships no CSS rules for them. The layout uses CSS Grid with
 * grid-template-columns: repeat(12, ...) so each vf-col-N needs
 * grid-column: span N to actually occupy the right number of columns.
 */
:deep(.vf-col-0)  { grid-column: span 0; }
:deep(.vf-col-1)  { grid-column: span 1; }
:deep(.vf-col-2)  { grid-column: span 2; }
:deep(.vf-col-3)  { grid-column: span 3; }
:deep(.vf-col-4)  { grid-column: span 4; }
:deep(.vf-col-5)  { grid-column: span 5; }
:deep(.vf-col-6)  { grid-column: span 6; }
:deep(.vf-col-7)  { grid-column: span 7; }
:deep(.vf-col-8)  { grid-column: span 8; }
:deep(.vf-col-9)  { grid-column: span 9; }
:deep(.vf-col-10) { grid-column: span 10; }
:deep(.vf-col-11) { grid-column: span 11; }
:deep(.vf-col-12) { grid-column: span 12; }
</style>
